// The coarse/detail/evicted tier indicator (`client-rounds-review`, **D6**; ported from
// `prototype/sketches/rounds-slice/detail-degradation.html`).
//
// The component's whole job is to render a `TierStateDescriptor` in ONE neutral chrome
// treatment. It deliberately switches on nothing:
//
//   * **No state is colour-privileged.** The sketch's first attempt gave the healthy
//     `Detail` state an `--info` pill; that was dropped on reflection (**D6**,
//     `prototypes.md`) because colouring the good state trains the eye to read the
//     neutral one as degraded — the wrong lesson when coarse is a legitimate way to
//     watch a match at any zoom.
//   * **No state uses `--danger`.** None of them is a failure — `evicted` included: those
//     bytes were deliberately reclaimed to make room for newer rounds, not damaged.
//     Corruption is a genuinely different thing and is surfaced elsewhere, as a real error.
//   * **The third state dropped in without rework, exactly as designed.** Eviction landed
//     with `rounds-trace-http`, and `evicted`/"Coarse only" cost **one row** in
//     `TIER_STATES` (`review-model.ts`) and not a single line of this file — because the
//     chrome lives here and the *words and edge* live in the descriptor. Keep it that way:
//     a fourth state is another row, never a branch in this component.

import type { CSSProperties } from 'react';
import type { TierStateDescriptor } from '../../rounds/review-model';

export interface TierIndicatorProps {
  /** The state to render. Any descriptor — the component knows no closed set. */
  state: TierStateDescriptor;
}

/** The single neutral treatment every state gets. The only per-state variation is the
 * border *style* (`solid`/`dashed`), which carries no colour weight. */
function pillStyle(edge: TierStateDescriptor['edge']): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '5px 10px',
    borderRadius: 'var(--r-pill, 999px)',
    border: `1.5px ${edge} var(--hud-line)`,
    background: 'var(--hud-bg)',
    color: 'var(--hud-text-dim)',
    fontFamily: 'var(--font-display)',
    fontSize: 12,
    whiteSpace: 'nowrap',
  };
}

function dotStyle(edge: TierStateDescriptor['edge']): CSSProperties {
  return edge === 'dashed'
    ? {
        width: 7,
        height: 7,
        borderRadius: 4,
        background: 'transparent',
        border: '1.5px dashed var(--hud-text-dim)',
        flex: 'none',
      }
    : { width: 7, height: 7, borderRadius: 4, background: 'var(--hud-text-dim)', flex: 'none' };
}

export function TierIndicator({ state }: TierIndicatorProps) {
  return (
    <span data-testid="review-tier" data-tier={state.id} title={state.hint} style={pillStyle(state.edge)}>
      <span data-testid="review-tier-dot" aria-hidden style={dotStyle(state.edge)} />
      {state.label}
    </span>
  );
}
